import { CSSProperties, useEffect, useMemo, useState } from 'react';
import { Button, Empty, Select, Typography } from 'antd';
import {
	TrackedCard,
	Area as SlotArea,
	KtStagingArea,
	useListTrackedCardsQuery,
	useStatusLegendQuery,
} from '../../redux/api/kit-staging';
import { OliGroup } from '../../pages/KitStaging/boards/groupByOli';
import { CardDetailExplanation } from './CardDetailExplanation';
import { CardMovementsLog } from './CardMovementsLog';
import { GroupActionBlock } from './GroupActionBlock';
import { Area, lookup as lookupRule } from './moveRulesClient';

/** Same-app relative link to an OLI's detail page (id = OLI UUID). The boards
 *  live inside Fastlane, so no env var is needed — returns null when the uuid
 *  hasn't synced yet. */
function erpOliUrl(uuid: string | null | undefined): string | null {
	if (!uuid) return null;
	return `/orders/line-items/${uuid}/`;
}

/** Pick the first valid free shelf for a move into JSA or MSA.
 *
 * JSA priority (matches the manual picker's sort):
 *   1. The partner card's shelf (kit↔material share a JSA shelf).
 *   2. First free shelf in any MG-matched JSA area.
 *   3. First free shelf in any active JSA area (fallback).
 * Occupancy uses item_type — kit and material may share a JSA shelf.
 *
 * MSA priority:
 *   1. First free shelf in any active MSA area.
 * Occupancy ignores item_type — one card per MSA shelf.
 *
 * Returns ``null`` only when there's no free shelf anywhere.
 */
function pickFirstValidShelf(
	card: TrackedCard,
	target: 'msa' | 'jsa',
	allCards: readonly TrackedCard[],
	stagingAreas: readonly KtStagingArea[],
): number | null {
	const areaType = target === 'jsa' ? 'job_staging' : 'material_staging';
	const areas = stagingAreas.filter((a) => a.area_type === areaType && a.is_active);
	if (areas.length === 0) return null;

	// Occupancy: JSA cares about item_type (kit + material may co-occupy);
	// MSA does not (one card per MSA shelf).
	const occupied = new Set<number>();
	for (const c of allCards) {
		if (
			c.current_area === target &&
			c.current_shelf != null &&
			c.id !== card.id &&
			(target === 'msa' || c.item_type === card.item_type)
		) {
			occupied.add(c.current_shelf);
		}
	}

	// JSA-only: prefer the partner card's shelf so kit+material end up
	// co-located.
	if (target === 'jsa') {
		const partnerType = card.item_type === 'kit' ? 'material' : 'kit';
		const partner = allCards.find(
			(c) =>
				c.order_line_item_id === card.order_line_item_id &&
				c.item_type === partnerType &&
				c.current_area === 'jsa' &&
				c.current_shelf != null,
		);
		if (partner?.current_shelf != null && partner.current_shelf !== card.current_shelf) {
			return partner.current_shelf;
		}
	}

	// For JSA, prefer MG-matched areas first; for MSA there's no MG match.
	let orderedAreas = areas;
	if (target === 'jsa') {
		const mgName = card.job?.machine_group?.name;
		const mgMatched = mgName ? areas.filter((a) => a.machine_groups.some((mg) => mg.name === mgName)) : [];
		orderedAreas = mgMatched.length ? [...mgMatched, ...areas.filter((a) => !mgMatched.includes(a))] : areas;
	}

	for (const a of orderedAreas) {
		for (const r of a.racks) {
			for (const s of r.shelves) {
				if (!s.is_active) continue;
				if (card.current_shelf != null && s.id === card.current_shelf) continue;
				if (occupied.has(s.id)) continue;
				return s.id;
			}
		}
	}
	return null;
}

interface Props {
	selectedGroup: OliGroup | null;
	lastSyncedAt: number | null;
	stagingAreas: KtStagingArea[];
	activeTab?: string;
	onInitiate: (card: TrackedCard, target_area: SlotArea | 'machine', target_shelf?: number) => void;
	onConfirm: (card: TrackedCard, target_area?: SlotArea | 'machine', target_shelf?: number) => void;
	onUndo: (card: TrackedCard) => void;
	onDelete: (card: TrackedCard) => void;
	onMarkPickedUp?: (card: TrackedCard) => void;
}

function useSyncedAgo(lastSyncedAt: number | null): string {
	const [now, setNow] = useState(Date.now());
	useEffect(() => {
		const id = setInterval(() => setNow(Date.now()), 5000);
		return () => clearInterval(id);
	}, []);
	if (!lastSyncedAt) return '—';
	const seconds = Math.max(0, Math.round((now - lastSyncedAt) / 1000));
	if (seconds < 60) return `${seconds}s ago`;
	const mins = Math.round(seconds / 60);
	return `${mins}m ago`;
}

type ShelfPick = { card: TrackedCard; target: 'msa' | 'jsa' } | null;
type OverridePick = TrackedCard | null;

const ALL_DESTINATIONS: Array<'cart' | 'msa' | 'jsa' | 'dekit' | 'unassigned' | 'machine'> = [
	'cart',
	'msa',
	'jsa',
	'dekit',
	'unassigned',
	'machine',
];

export function TabletSidebar(props: Props) {
	const { selectedGroup, lastSyncedAt, stagingAreas, activeTab, onInitiate, onConfirm, onUndo, onDelete, onMarkPickedUp } =
		props;
	const { data: legend } = useStatusLegendQuery(undefined, { pollingInterval: 10000 });
	const { data: allCards } = useListTrackedCardsQuery({}, { pollingInterval: 10000 });
	const syncedAgo = useSyncedAgo(lastSyncedAt);
	const [shelfPick, setShelfPick] = useState<ShelfPick>(null);
	const [pickedShelfId, setPickedShelfId] = useState<number | null>(null);
	const [overridePick, setOverridePick] = useState<OverridePick>(null);

	// Drop the inline pickers if the selected card changes/clears.
	useEffect(() => {
		setShelfPick(null);
		setPickedShelfId(null);
		setOverridePick(null);
	}, [selectedGroup?.oli]);

	const shelfOptions = useMemo(() => {
		if (!shelfPick) return [];
		const { card, target } = shelfPick;
		const areaType = target === 'jsa' ? 'job_staging' : 'material_staging';
		const areas = (stagingAreas ?? []).filter((a) => a.area_type === areaType && a.is_active);
		const mgName = card.job?.machine_group?.name;
		const relevant =
			target === 'jsa' && mgName ? areas.filter((a) => a.machine_groups.some((mg) => mg.name === mgName)) : areas;
		// Find the partner card (same OLI, different item_type) — if it's on a
		// shelf in this target area, flag that shelf as "partner is here" so the
		// operator picks the same one for shared-shelf placement.
		const partnerType = card.item_type === 'kit' ? 'material' : 'kit';
		const partnerCard = (allCards ?? []).find(
			(c) =>
				c.order_line_item_id === card.order_line_item_id &&
				c.item_type === partnerType &&
				c.current_area === target &&
				c.current_shelf != null,
		);
		const partnerShelfId = partnerCard?.current_shelf ?? null;
		const partnerLabel = card.item_type === 'kit' ? 'MATERIAL' : 'KIT';
		// Only TrackedCard-derived occupancy — ignore the legacy `is_occupied`
		// field on shelves (it reflects StagingAssignment, not TrackedCards).
		const occupied = new Set<number>();
		(allCards ?? []).forEach((c) => {
			if (
				c.current_area === target &&
				c.current_shelf != null &&
				c.id !== card.id &&
				(target === 'msa' || c.item_type === card.item_type)
			) {
				occupied.add(c.current_shelf);
			}
		});
		const opts: { value: number; label: string; disabled?: boolean; isPartner?: boolean }[] = [];
		for (const a of relevant) {
			for (const r of a.racks) {
				for (const s of r.shelves) {
					if (!s.is_active) continue;
					// Skip the card's current shelf — it's not a valid target.
					if (card.current_shelf != null && s.id === card.current_shelf) continue;
					const taken = occupied.has(s.id);
					const isPartner = s.id === partnerShelfId;
					const suffix = isPartner ? `  ← ${partnerLabel} HERE` : taken ? ' (occupied)' : '';
					opts.push({
						value: s.id,
						label: `${a.name} · ${r.label} · #${s.position}${suffix}`,
						// The partner shelf is the recommended target — never disable it.
						disabled: taken && !isPartner,
						isPartner,
					});
				}
			}
		}
		// Sort: partner shelf first, then free shelves, then occupied at the end.
		opts.sort((a, b) => {
			if (a.isPartner !== b.isPartner) return a.isPartner ? -1 : 1;
			if (a.disabled !== b.disabled) return a.disabled ? 1 : -1;
			return 0;
		});
		return opts;
	}, [shelfPick, stagingAreas, allCards]);

	if (selectedGroup) {
		const { kitCard, materialCard, job, oli } = selectedGroup;
		const representative = kitCard ?? materialCard;
		return (
			<aside style={sidebarStyle}>
				<div style={{ padding: 10 }}>
					<Typography.Text type="secondary" style={{ fontSize: 11, fontWeight: 500 }}>
						{job?.job_number || `OLI #${oli}`}
					</Typography.Text>
					{job?.part_name &&
						(() => {
							const href = erpOliUrl(job?.oli_uuid);
							const titleStyle: CSSProperties = {
								margin: '2px 0 0 0',
								fontSize: 16,
								fontWeight: 700,
								wordBreak: 'break-word',
							};
							if (!href) {
								return (
									<Typography.Title level={5} style={{ ...titleStyle, color: '#111' }}>
										{job.part_name}
									</Typography.Title>
								);
							}
							return (
								<Typography.Title level={5} style={titleStyle}>
									<a
										href={href}
										target="_blank"
										rel="noopener noreferrer"
										title={`Open OLI #${oli} in Fastlane`}
										style={{ color: '#1890ff', textDecoration: 'underline' }}
									>
										{job.part_name}
									</a>
								</Typography.Title>
							);
						})()}
					{representative &&
						!shelfPick &&
						!overridePick &&
						(() => {
							// Resolve pending_target_shelf -> "Area · Rack · Shelf" label.
							const pendingShelfId = representative.pending_target_shelf;
							let pendingLabel: string | null = null;
							if (pendingShelfId != null) {
								outer: for (const a of stagingAreas ?? []) {
									for (const r of a.racks ?? []) {
										for (const s of r.shelves ?? []) {
											if (s.id === pendingShelfId) {
												pendingLabel = `${a.name} · Rack ${r.label} · Shelf ${s.position}`;
												break outer;
											}
										}
									}
								}
							}
							return (
								<div style={{ marginTop: 8 }}>
									<CardDetailExplanation
										explanation={representative.status_explanation}
										pendingTargetArea={representative.pending_target_area}
										pendingTargetLabel={pendingLabel}
									/>
								</div>
							);
						})()}
					{overridePick ? (
						<InlineOverridePicker
							card={overridePick}
							onCancel={() => setOverridePick(null)}
							onPick={(target) => {
								if (target === 'msa' || target === 'jsa') {
									// Pivot into the shelf picker for this card+target.
									setShelfPick({ card: overridePick, target });
									setPickedShelfId(null);
									setOverridePick(null);
									return;
								}
								// Shelf-less destinations (UNASSIGNED, CART, DEKIT, MACHINE):
								// dispatch by matrix rule.
								const from = overridePick.current_area as Area;
								const rule = lookupRule(from, target);
								if (rule === 'SINGLE' || rule === 'SINGLE_ERP') {
									onConfirm(overridePick, target);
								} else if (rule === 'DUAL' || rule === 'DUAL_LOCAL') {
									onInitiate(overridePick, target);
								}
								setOverridePick(null);
							}}
						/>
					) : shelfPick ? (
						<div style={{ marginTop: 12 }}>
							<Typography.Text strong style={{ fontSize: 12 }}>
								Pick a {shelfPick.target.toUpperCase()} shelf for{' '}
								{shelfPick.card.item_type === 'kit' ? 'kit' : 'material'}
							</Typography.Text>
							<Select
								value={pickedShelfId ?? undefined}
								onChange={(v) => setPickedShelfId(v ?? null)}
								placeholder={shelfOptions.length === 0 ? 'No shelves available' : 'Pick a shelf'}
								options={shelfOptions}
								style={{ width: '100%', marginTop: 6 }}
								allowClear
								showSearch
								optionFilterProp="label"
								disabled={shelfOptions.length === 0}
								size="middle"
							/>
							{shelfOptions.length === 0 && (
								<Typography.Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 11 }}>
									No active {shelfPick.target.toUpperCase()} shelves match this card.
								</Typography.Text>
							)}
							<div style={{ marginTop: 10, display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 6 }}>
								<Button
									type="primary"
									disabled={pickedShelfId == null}
									style={{ height: 38, fontWeight: 600 }}
									onClick={() => {
										if (pickedShelfId == null) return;
										// Same-area reshelve (MSA→MSA / JSA→JSA) is SINGLE rule —
										// backend rejects initiate for SINGLE. Cross-area moves
										// (UNASSIGNED/CART → MSA/JSA) are DUAL — initiate first.
										const from = shelfPick.card.current_area as Area;
										const rule = lookupRule(from, shelfPick.target, shelfPick.card.status);
										if (rule === 'SINGLE' || rule === 'SINGLE_ERP') {
											onConfirm(shelfPick.card, shelfPick.target, pickedShelfId);
										} else {
											onInitiate(shelfPick.card, shelfPick.target, pickedShelfId);
										}
										setShelfPick(null);
										setPickedShelfId(null);
									}}
								>
									Confirm
								</Button>
								<Button
									style={{ height: 38 }}
									onClick={() => {
										setShelfPick(null);
										setPickedShelfId(null);
									}}
								>
									Cancel
								</Button>
							</div>
						</div>
					) : (
						<GroupActionBlock
							kitCard={kitCard}
							materialCard={materialCard}
							stagingAreas={stagingAreas}
							activeTab={activeTab}
							onInitiate={onInitiate}
							onConfirm={onConfirm}
							onUndo={onUndo}
							onOverride={(card) => setOverridePick(card)}
							onDelete={onDelete}
							onMarkPickedUp={
								onMarkPickedUp
									? () => {
											const target =
												kitCard?.current_area === 'dekit'
													? kitCard
													: materialCard?.current_area === 'dekit'
														? materialCard
														: null;
											if (target) onMarkPickedUp(target);
										}
									: undefined
							}
							onPickShelf={(card, target) => {
								// Any move TO jsa or msa auto-assigns the first valid
								// free shelf. Skips the manual picker entirely —
								// covers Cart→{JSA,MSA}, Unassigned→{JSA,MSA},
								// JSA→JSA reshelve, MSA→MSA reshelve, MSA→JSA, etc.
								if (target === 'jsa' || target === 'msa') {
									const auto = pickFirstValidShelf(card, target, allCards ?? [], stagingAreas ?? []);
									if (auto != null) {
										const from = card.current_area as Area;
										// Pass card.status so the rule lookup honours the
										// status-conditional overrides — JSA→JSA on a
										// site-mismatch card returns DUAL (deliberate
										// reroute), not SINGLE.
										const rule = lookupRule(from, target, card.status);
										if (rule === 'SINGLE' || rule === 'SINGLE_ERP') {
											onConfirm(card, target, auto);
										} else {
											onInitiate(card, target, auto);
										}
										return;
									}
									// Fall through to picker only if no valid shelf exists
									// anywhere — extreme edge case.
								}
								setShelfPick({ card, target });
								setPickedShelfId(null);
							}}
							onAutoPlaceShelf={(card, target, shelfId) => {
								// Skip the picker — fire the move straight through.
								const from = card.current_area as Area;
								const rule = lookupRule(from, target, card.status);
								if (rule === 'SINGLE' || rule === 'SINGLE_ERP') {
									onConfirm(card, target, shelfId);
								} else {
									onInitiate(card, target, shelfId);
								}
							}}
						/>
					)}
					{oli != null && !shelfPick && !overridePick && <CardMovementsLog oli={oli} />}
				</div>
			</aside>
		);
	}

	return (
		<aside style={sidebarStyle}>
			<div style={{ padding: 10 }}>
				<div>
					<Typography.Text strong style={{ fontSize: 11, color: '#8c8c8c', letterSpacing: 0.5 }}>
						SYNC
					</Typography.Text>
					<div style={{ display: 'flex', alignItems: 'center', gap: 6, fontSize: 11, marginTop: 4 }}>
						<span
							style={{
								width: 8,
								height: 8,
								borderRadius: '50%',
								background: lastSyncedAt ? '#52c41a' : '#bfbfbf',
							}}
						/>
						Synced {syncedAgo}
					</div>
				</div>

				<details style={{ marginTop: 12 }}>
					<summary
						style={{
							cursor: 'pointer',
							fontSize: 11,
							color: '#8c8c8c',
							letterSpacing: 0.5,
							fontWeight: 700,
							userSelect: 'none',
						}}
					>
						STATUS LEGEND
					</summary>
					<div style={{ marginTop: 6, display: 'flex', flexDirection: 'column', gap: 4 }}>
						{(legend ?? []).map((entry) => (
							<div key={entry.status} style={{ fontSize: 11, color: '#555' }}>
								<span style={{ fontWeight: 600 }}>{entry.label}</span> — {entry.description}
							</div>
						))}
						{(!legend || legend.length === 0) && <Empty image={Empty.PRESENTED_IMAGE_SIMPLE} description="—" />}
					</div>
				</details>

				<div style={{ marginTop: 20, fontSize: 11, color: '#bfbfbf', fontStyle: 'italic' }}>
					Tap a card to see actions.
				</div>
			</div>
		</aside>
	);
}

const sidebarStyle: CSSProperties = {
	width: 280,
	flexShrink: 0,
	background: '#fafafa',
	borderRight: '1px solid #e5e7eb',
	overflowY: 'auto',
};

type Destination = 'cart' | 'msa' | 'jsa' | 'dekit' | 'unassigned' | 'machine';

function InlineOverridePicker({
	card,
	onPick,
	onCancel,
}: {
	card: TrackedCard;
	onPick: (target: Destination) => void;
	onCancel: () => void;
}) {
	const from = card.current_area as Area;
	const onHold = card.status === 'on_hold';
	const cancelled = card.status === 'cancelled';
	const siteMismatch = card.status === 'site_mismatch';
	const taskMismatch = card.status === 'task_status_mismatch';
	const job = card.job;
	const kitReady = !!job?.kit_ready;
	const materialReady = !!job?.material_ready;
	const materialPartial = !!job?.material_partial;

	// Spec gates per destination — mirrors backend _validate_readiness.
	const readinessOk = (d: Destination) => {
		if (d === 'jsa') return kitReady;
		if (d === 'msa') return materialReady || materialPartial;
		if (d === 'cart') return kitReady || materialReady || materialPartial;
		if (d === 'machine') return kitReady;
		return true; // unassigned, dekit
	};

	// Per-status destination gates (Roy Q4.1/Q4.2/Q4.3/Q2.3):
	const allowedByStatus = (d: Destination) => {
		if (cancelled) return d === 'dekit';
		if (siteMismatch) return d === 'jsa' || d === 'dekit' || d === 'unassigned';
		if (taskMismatch) return d === 'machine' || d === 'dekit' || d === 'unassigned';
		if (onHold) return d === 'dekit' || d === 'unassigned';
		return true;
	};

	const destinations = ALL_DESTINATIONS.filter((d) => {
		if (lookupRule(from, d) === 'NOT_ALLOWED') return false;
		if (!readinessOk(d)) return false;
		if (!allowedByStatus(d)) return false;
		return true;
	});

	return (
		<div style={{ marginTop: 12 }}>
			<Typography.Text strong style={{ fontSize: 12 }}>
				Move {card.item_type === 'kit' ? 'kit' : 'material'} to…
			</Typography.Text>
			{destinations.length === 0 ? (
				<Typography.Text type="secondary" style={{ display: 'block', marginTop: 6, fontSize: 11 }}>
					No legal destinations from <code>{from}</code>.
				</Typography.Text>
			) : (
				<div
					style={{
						marginTop: 8,
						display: 'grid',
						gridTemplateColumns: '1fr 1fr',
						gap: 6,
					}}
				>
					{destinations.map((d) => (
						<Button key={d} style={{ height: 38, fontWeight: 600 }} onClick={() => onPick(d)}>
							{d.toUpperCase()}
						</Button>
					))}
				</div>
			)}
			<Button style={{ height: 32, marginTop: 8, width: '100%' }} onClick={onCancel}>
				Cancel
			</Button>
		</div>
	);
}
